import React, { useEffect, useCallback, useState, useRef } from 'react';

import * as Yup from 'yup';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Button, Row, Col, Container } from 'react-bootstrap';
import { addMinutes, parseISO, format } from 'date-fns';

import { useToast } from '../../hooks/toast';

import api from '../../services/api';

import SelectSync, { IOption } from '../SelectSync';
import Input from '../Input';
import Loading from '../Loading';

import {
  CONFIRMACAO_AGENDA,
  optionsHorasAtendimento,
  STATUS_AGENDA,
} from '../../utils/funcoes';
import getValidationErrors from '../../utils/getValidationErrors';
import getResponseErrors from '../../utils/getResponseErrors';
import { IPedidoExames } from '../../pages/PedidoExamesBusca';

interface IProcedimento {
  ProCodigo: number;
  ProValor: number;
  ProNome: string;
}

interface IFormData {
  AgeConfCodigo: number;
  AgeData: Date;
  AgeHora: string;
}

interface IReturnAgendamento {
  AgeCodigo: number;
  AgeConfCodigo: number;
}

type ComponentProps = {
  PedCodigo: number;
  handleFechar(dados: IReturnAgendamento): void;
};

const PedidoExame: React.FC<ComponentProps> = ({ PedCodigo, handleFechar }) => {
  const formRef = useRef<FormHandles>(null);
  const { addToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [pedidoExame, setPedidoExame] = useState<IPedidoExames>();
  const [procedimentos, setProcedimentos] = useState<IProcedimento[]>();
  const [optionsAgendas, setOptionsAgendas] = useState<IOption[]>([]);
  const [finalizado, setFinalizado] = useState(false);
  const [optionsHora, _] = useState(() => optionsHorasAtendimento());
  const initialData = { AgeData: format(new Date(), 'yyyy-MM-dd') };

  useEffect(() => {
    async function loadDados(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IPedidoExames>(
          `pedidoexames/${PedCodigo}?limite=1000`,
        );
        if (response.data.PedFinalizado) {
          setFinalizado(true);
        } else {
          const responseAgendas = await api.get<IOption[]>(
            `agendas/porperfil?ConPerCodigo=${response.data.PedPerCodigo}&ConClinica=${response.data.PedCliCodigo}`,
          );

          setOptionsAgendas(responseAgendas.data);

          const responseProcedimentos = await api.get<IProcedimento[]>(
            `procedimentos/porcodigos?codigos=${response.data.itens
              .map(ite => ite.ItmProCodigo)
              .join()}`,
          );
          setProcedimentos(responseProcedimentos.data);
        }

        setPedidoExame(response.data);
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        addToast({
          title: 'Não foi possível carregar disponibilidade',
          type: 'error',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    }
    if (PedCodigo) loadDados();
  }, [PedCodigo, addToast]);

  const handleSubmit = useCallback(
    async (dados: IFormData) => {
      setLoading(true);
      try {
        if (!pedidoExame) throw new Error('Pedido de Exame não encontrado');
        if (!procedimentos) throw new Error('Procedimentos não encontrados');

        formRef.current?.setErrors({});
        const schema = Yup.object().shape({
          AgeConfCodigo: Yup.number().required(),
          AgeData: Yup.date().required(),
          AgeHora: Yup.string().required('Hora obrigatório'),
        });

        await schema.validate(dados, {
          abortEarly: false,
        });

        const dataAgendamento = parseISO(
          `${dados.AgeData} ${dados.AgeHora}:00`,
        );

        const agendamento = {
          AgeConfCodigo: dados.AgeConfCodigo,
          AgeInicio: dataAgendamento,
          AgeFim: addMinutes(dataAgendamento, 5),
          AgePacCodigo: pedidoExame.PedPacCodigo,
          AgeFormaPag: 9,
          AgeClinica: pedidoExame.PedCliCodigo,
          AgeObs: '',
          AgeStatus: STATUS_AGENDA.AGENDADO,
          AgeConfirmado: CONFIRMACAO_AGENDA.AGUARDANDO_CONFIRMACAO,
          AgePlano: false,
          duracao: 5,
          procedimentos: procedimentos.map(pro => {
            return {
              ProCodigo: pro.ProCodigo,
              ProNome: pro.ProNome,
              ProValor: pro.ProValor,
            };
          }),
          PedCodigo: pedidoExame.PedCodigo,
        };

        const appointment = await api.post<IReturnAgendamento>(
          '/agendamentos',
          agendamento,
        );
        // pode pegar o retorno e jogar para a agenda
        addToast({ title: 'Agendamento salvo', type: 'success' });
        handleFechar({
          AgeCodigo: appointment.data.AgeCodigo,
          AgeConfCodigo: appointment.data.AgeConfCodigo,
        });
      } catch (error) {
        if (error instanceof Yup.ValidationError) {
          const errors = getValidationErrors(error);

          formRef.current?.setErrors(errors);
          return;
        }
        const mensagemErro = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Houve um erro ao salvar agendamento',
          description: mensagemErro,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast, pedidoExame, procedimentos, handleFechar],
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  const handleOnChangeVazio = useCallback(() => {}, []);

  return (
    <Container className="my-4">
      {loading && <Loading />}

      {finalizado ? (
        <h5 className="text-center">Status: Pedido de Exame Finalizado</h5>
      ) : (
        <>
          <h5>Gerar agendamento</h5>
          <Form ref={formRef} initialData={initialData} onSubmit={handleSubmit}>
            <Row className="mb-2">
              <Col md={4} sm={6}>
                <SelectSync
                  name="AgeConfCodigo"
                  label="Selecione a Agenda"
                  // isClearable={false}
                  placeholder="Selecione"
                  handleSelectChange={handleOnChangeVazio}
                  options={optionsAgendas}
                />
              </Col>
              <Col md={3} sm={6}>
                <Input name="AgeData" label="Data" type="date" />
              </Col>
              <Col md={3} sm={6}>
                <SelectSync
                  name="AgeHora"
                  label="Hora"
                  placeholder="Selecione"
                  isClearable={false}
                  handleSelectChange={handleOnChangeVazio}
                  options={optionsHora}
                />
              </Col>
              <Col md={2} sm={6}>
                <br />
                <Button
                  type="submit"
                  size="sm"
                  className="mt-1"
                  disabled={loading}
                >
                  Gerar
                </Button>
              </Col>
            </Row>
          </Form>
        </>
      )}
    </Container>
  );
};

export default PedidoExame;
